import { useCallback, useRef, useState } from 'react'

/**
 * The pixel size of an element, kept current as the screen resizes. The wind chart draws itself at its
 * real size, so it can fill whatever room the one-screen layout leaves it while its labels stay sharp and
 * unstretched. Null until measured (and during server rendering in tests).
 *
 * A callback ref, not useRef + useEffect: the chart only appears once there is data, after the first
 * render, and an effect that ran on mount would never see it.
 */
export function useSize<T extends HTMLElement>(): [(el: T | null) => void, { width: number; height: number } | null] {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)
  const observer = useRef<ResizeObserver | null>(null)
  const ref = useCallback((el: T | null) => {
    observer.current?.disconnect()
    observer.current = null
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return
      const { width, height } = entry.contentRect
      setSize((prev) => (prev && Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1 ? prev : { width, height }))
    })
    ro.observe(el)
    observer.current = ro
  }, [])
  return [ref, size]
}
